import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Edit, Trash2, Plus, Save, X } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SeasonsManager = () => {
  const [seasons, setSeasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    display_name: '',
    catalog_url: '',
    pricelist_url: '',
    is_active: true,
    order: 0
  });

  useEffect(() => {
    fetchSeasons();
  }, []);

  const fetchSeasons = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/seasons`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSeasons(response.data);
    } catch (error) {
      toast.error('Error al cargar temporadas');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      
      if (editingId) {
        // Update existing season
        await axios.put(
          `${API}/admin/seasons/${editingId}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Temporada actualizada correctamente');
      } else {
        // Create new season
        await axios.post(
          `${API}/admin/seasons`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Temporada creada correctamente');
      }
      
      // Reset form and refresh list
      setFormData({
        name: '',
        display_name: '',
        catalog_url: '',
        pricelist_url: '',
        is_active: true,
        order: 0
      });
      setEditingId(null);
      setShowAddForm(false);
      fetchSeasons();
    } catch (error) {
      toast.error('Error al guardar temporada');
      console.error(error);
    }
  };

  const handleEdit = (season) => {
    setFormData({
      name: season.name,
      display_name: season.display_name,
      catalog_url: season.catalog_url,
      pricelist_url: season.pricelist_url,
      is_active: season.is_active,
      order: season.order
    });
    setEditingId(season.id);
    setShowAddForm(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('¿Estás seguro de que quieres eliminar esta temporada?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/seasons/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Temporada eliminada correctamente');
      fetchSeasons();
    } catch (error) {
      toast.error('Error al eliminar temporada');
    }
  };

  const handleCancel = () => {
    setFormData({
      name: '',
      display_name: '',
      catalog_url: '',
      pricelist_url: '',
      is_active: true,
      order: 0
    });
    setEditingId(null);
    setShowAddForm(false);
  };

  if (loading) {
    return <div className="text-center py-8">Cargando temporadas...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Gestión de Temporadas</h2>
        {!showAddForm && (
          <Button onClick={() => setShowAddForm(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Nueva Temporada
          </Button>
        )}
      </div>

      {showAddForm && (
        <div className="bg-white p-6 rounded-lg border shadow-sm">
          <h3 className="text-lg font-semibold mb-4">
            {editingId ? 'Editar Temporada' : 'Nueva Temporada'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Código (ej: AW25, SS26)</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value.toUpperCase() })}
                  placeholder="AW25"
                  required
                />
              </div>
              <div>
                <Label htmlFor="display_name">Nombre para mostrar</Label>
                <Input
                  id="display_name"
                  value={formData.display_name}
                  onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                  placeholder="Otoño/Invierno 2025"
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="catalog_url">URL del Catálogo (PDF)</Label>
              <Input
                id="catalog_url"
                value={formData.catalog_url}
                onChange={(e) => setFormData({ ...formData, catalog_url: e.target.value })}
                placeholder="https://..."
                required
              />
            </div>

            <div>
              <Label htmlFor="pricelist_url">URL de la Tarifa/Pricelist (XLS/XLSX)</Label>
              <Input
                id="pricelist_url"
                value={formData.pricelist_url}
                onChange={(e) => setFormData({ ...formData, pricelist_url: e.target.value })}
                placeholder="https://..."
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="order">Orden (menor = primero)</Label>
                <Input
                  id="order"
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                  required
                />
              </div>
              <div className="flex items-center space-x-2 pt-8">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-4 h-4"
                />
                <Label htmlFor="is_active">Activa (visible en la web)</Label>
              </div>
            </div>

            <div className="flex space-x-2">
              <Button type="submit">
                <Save className="w-4 h-4 mr-2" />
                {editingId ? 'Actualizar' : 'Crear'}
              </Button>
              <Button type="button" variant="outline" onClick={handleCancel}>
                <X className="w-4 h-4 mr-2" />
                Cancelar
              </Button>
            </div>
          </form>
        </div>
      )}

      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Temporadas Existentes</h3>
        {seasons.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No hay temporadas configuradas</p>
        ) : (
          <div className="grid gap-4">
            {seasons.map((season) => (
              <div
                key={season.id}
                className="bg-white p-4 rounded-lg border shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <span className="font-bold text-lg">{season.name}</span>
                      <span className="text-gray-600">{season.display_name}</span>
                      {season.is_active ? (
                        <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                          Activa
                        </span>
                      ) : (
                        <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded">
                          Inactiva
                        </span>
                      )}
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                        Orden: {season.order}
                      </span>
                    </div>
                    <div className="space-y-1 text-sm text-gray-600">
                      <div>
                        <span className="font-semibold">📕 Catálogo:</span>{' '}
                        <a
                          href={season.catalog_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline"
                        >
                          {season.catalog_url}
                        </a>
                      </div>
                      <div>
                        <span className="font-semibold">📊 Tarifa:</span>{' '}
                        <a
                          href={season.pricelist_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline"
                        >
                          {season.pricelist_url}
                        </a>
                      </div>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(season)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(season.id)}
                    >
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SeasonsManager;
